/**
 * Singleton manager around the measurement-points calculation worker.
 *
 * Callers get a `{ promise, cancel }` pair (same ergonomics as
 * heatmapWorkerManager): cancelling drops the pending callback so a stale
 * result from a superseded recompute is ignored. Falls back to running on the
 * main thread if the worker fails to initialise.
 *
 * The engine keeps a geometry-keyed cache of the sampled grid + occlusion BVH
 * inside the worker so repeated recomputes (e.g. dragging a lamp) skip the heavy
 * sampling/BVH build. A synchronous worker can't be interrupted mid-run, so when
 * a run must be SUPERSEDED (`recycle`) we terminate + respawn the worker — this
 * abandons the stale computation instead of letting it hog the thread. The cache
 * is lost on recycle, but recycles are rare (the runner coalesces bursts), so the
 * common "settle then recompute" path stays warm.
 */
import type { CalcInput, CalcOutput } from '../utils/measurementCalculation/types';
import type { CalcRequest, CalcResponse } from './measurementWorker';

interface CalcHandle {
  promise: Promise<CalcOutput>;
  cancel: () => void;
}

/** Rejection used when a run is abandoned because a newer one superseded it. */
export class MeasurementCalcSuperseded extends Error {
  constructor() {
    super('measurement calculation superseded');
    this.name = 'MeasurementCalcSuperseded';
  }
}

class MeasurementWorkerManager {
  private worker: Worker | null = null;
  private pending = new Map<string, { resolve: (r: CalcOutput) => void; reject: (e: Error) => void }>();
  private seq = 0;

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    try {
      this.worker = new Worker(new URL('./measurementWorker.ts', import.meta.url), {
        type: 'module',
        name: 'measurement-worker',
      });
      this.worker.onmessage = this.handleMessage.bind(this);
      this.worker.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('MeasurementWorkerManager: failed to init worker:', error);
      this.worker = null;
    }
  }

  private handleMessage(event: MessageEvent<CalcResponse>) {
    const msg = event.data;
    const entry = this.pending.get(msg.id);
    if (!entry) return; // cancelled or unknown
    this.pending.delete(msg.id);
    if (msg.type === 'CALCULATE_DONE') {
      entry.resolve(msg.result);
    } else {
      entry.reject(new Error(msg.error));
    }
  }

  private handleError(event: ErrorEvent) {
    const err = new Error(event.message || 'measurement worker error');
    this.pending.forEach((entry) => entry.reject(err));
    this.pending.clear();
  }

  /**
   * Abandon any in-flight run by terminating + respawning the worker. Pending
   * promises reject with `MeasurementCalcSuperseded` (callers should ignore it).
   */
  recycle(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    const err = new MeasurementCalcSuperseded();
    this.pending.forEach((entry) => entry.reject(err));
    this.pending.clear();
    this.initWorker();
  }

  /** Run a measurement calculation; resolves with `MeasurementData[]`. */
  calculate(input: CalcInput): CalcHandle {
    const id = `calc_${(this.seq += 1)}`;

    if (!this.worker) {
      // Worker unavailable — run on the main thread as a fallback.
      const promise = import('../utils/measurementCalculation/engine').then((m) => m.runMeasurementCalculation(input));
      return { promise, cancel: () => undefined };
    }

    const worker = this.worker;
    const promise = new Promise<CalcOutput>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      const req: CalcRequest = { type: 'CALCULATE', id, input };
      worker.postMessage(req);
    });

    return { promise, cancel: () => this.pending.delete(id) };
  }
}

export const measurementWorkerManager = new MeasurementWorkerManager();
